// Rejestr ośrodka: jedna lista znalezisk i akcji na ośrodek, z historią zmian (backend: SiteIssuesController)
import axios from "axios";
import { ApiURI } from "../types/configs";
import { ActionStatus } from "../types/reportActions";
import { headers } from "./headers";
import type { SpreadsheetTable } from "./reportsApi";

export const SiteIssuesInstance = axios.create({
  baseURL: ApiURI + "SiteIssues/",
});

// Pola edytowalne (i zapisywane w historii)
export interface SiteIssueState {
  participantNumber: string;
  visitNumber: string | null;
  categoryId: number | null;
  description: string;
  identificationDate: string | null; // YYYY-MM-DD
  status: ActionStatus;
  closedDate: string | null;
  actionsTaken: string | null;
  responsiblePerson: string | null;
  identifiedBy: string | null;
  potentialDeviation: boolean;
  serious: boolean;
  seriousBreach: boolean;
  needsReview: boolean;
}

export interface SiteIssue extends SiteIssueState {
  id: string;
  resortId: string;
  category: string | null;
  reviewNote: string | null;
  openedInReportId: string | null;
  created: string;
  createdBy: string;
  edited: string;
  editedBy: string;
}

export interface SiteIssueList {
  resortId: string;
  resortName: string;
  studyId: string;
  studyName: string;
  items: SiteIssue[];
  snapshot: { source: string; takenAt: string; takenBy: string } | null;
}

export interface SiteIssueChange {
  id: string;
  action: "Create" | "Update" | "Delete" | "Migrate";
  changedFields: string[];
  state: SiteIssueState | null;
  changedAt: string;
  changedBy: string;
  reportId: string | null;
  reportName: string | null;
}

export interface ReviewResult {
  updated: number;
  errors: Record<string, string[]>;
}

const withReport = (reportId?: string) => (reportId ? { reportId } : undefined);

export const fetchResortIssues = async (resortId: string): Promise<SiteIssueList> =>
  (await SiteIssuesInstance.get<SiteIssueList>(`resorts/${resortId}`, headers())).data;

export const fetchIssueHistory = async (id: string): Promise<SiteIssueChange[]> =>
  (await SiteIssuesInstance.get<SiteIssueChange[]>(`${id}/history`, headers())).data;

export const createIssue = async (resortId: string, state: SiteIssueState, reportId?: string): Promise<string> =>
  (await SiteIssuesInstance.post<{ id: string }>(`resorts/${resortId}`, state, { ...headers(), params: withReport(reportId) })).data.id;

export const updateIssue = async (id: string, state: SiteIssueState, reportId?: string): Promise<void> => {
  await SiteIssuesInstance.put(id, state, { ...headers(), params: withReport(reportId) });
};

export const deleteIssue = async (id: string, reportId?: string): Promise<void> => {
  await SiteIssuesInstance.delete(id, { ...headers(), params: withReport(reportId) });
};

// Zbiorczo: close = zamknij z datą (i dopisz działania), inaczej potwierdź, że pozycje są otwarte
export const reviewIssues = async (
  ids: string[], close: boolean, closedDate?: string | null, actionsTaken?: string, reportId?: string
): Promise<ReviewResult> =>
  (await SiteIssuesInstance.post<ReviewResult>("review", { ids, close, closedDate, actionsTaken }, { ...headers(), params: withReport(reportId) })).data;

export const parseIssuesSpreadsheet = async (file: File): Promise<SpreadsheetTable> => {
  const form = new FormData();
  form.append("file", file);
  return (
    await SiteIssuesInstance.post<SpreadsheetTable>("import/parse", form, {
      headers: { Authorization: headers().headers.Authorization, "Content-Type": "multipart/form-data" },
    })
  ).data;
};

const blob = async (url: string): Promise<Blob> =>
  (await SiteIssuesInstance.get<Blob>(url, { responseType: "blob", headers: { Authorization: headers().headers.Authorization } })).data;

export const downloadIssuesTemplate = () => blob("template");
export const downloadResortIssues = (resortId: string) => blob(`resorts/${resortId}/export`);

// Rejestr ośrodka raportu: migawka (raport zaakceptowany, `snapshot` ustawione) albo stan bieżący
export const fetchReportIssues = async (reportId: string): Promise<SiteIssueList> =>
  (await SiteIssuesInstance.get<SiteIssueList>(`reports/${reportId}`, headers())).data;

// Liczniki rejestru dla ośrodków badania (strona badania)
export interface ResortIssueSummary {
  resortId: string;
  total: number;
  open: number;
  needsReview: number;
  seriousOpen: number;
}

export const fetchStudyIssueSummary = async (studyId: string): Promise<ResortIssueSummary[]> =>
  (await SiteIssuesInstance.get<ResortIssueSummary[]>(`studies/${studyId}/summary`, headers())).data;

// Rejestr badania (tylko odczyt): pozycje ze wszystkich ośrodków badania, nazwy ośrodków osobno
export interface StudySiteIssueList {
  studyId: string;
  studyName: string;
  resorts: { id: string; name: string }[];
  items: SiteIssue[];
}

export const fetchStudyIssues = async (studyId: string): Promise<StudySiteIssueList> =>
  (await SiteIssuesInstance.get<StudySiteIssueList>(`studies/${studyId}`, headers())).data;
